import { useEffect, useRef, useState } from 'react';

// Per-character reveal cadence for the chat-title typewriter. ~22ms/char keeps a
// typical generated title (30–50 chars) under ~1s while staying clearly
// animated.
const DEFAULT_CHAR_INTERVAL_MS = 22;

type Options = {
  // When false the full text is shown at once (no animation).
  enabled: boolean;
  charIntervalMs?: number;
  // Fired once the final character has been revealed.
  onDone?: () => void;
};

// Reveals `text` one character at a time while `enabled`. Returns the currently
// visible substring and whether the reveal has finished. Disabling (or an empty
// text) snaps straight to the full string.
export default function useTypewriter(text: string, options: Options) {
  const {
    enabled,
    charIntervalMs = DEFAULT_CHAR_INTERVAL_MS,
    onDone,
  } = options;
  const [count, setCount] = useState(() => (enabled ? 0 : text.length));
  // Keep the latest callback without re-arming the interval on every render.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!enabled) {
      setCount(text.length);
      return undefined;
    }
    if (!text) {
      setCount(0);
      onDoneRef.current?.();
      return undefined;
    }
    setCount(0);
    let revealed = 0;
    const timer = setInterval(() => {
      revealed += 1;
      setCount(revealed);
      if (revealed >= text.length) {
        clearInterval(timer);
        onDoneRef.current?.();
      }
    }, charIntervalMs);
    return () => clearInterval(timer);
  }, [text, enabled, charIntervalMs]);

  return {
    displayed: text.slice(0, count),
    done: count >= text.length,
  };
}
